import { GridComparatorFn } from "@mui/x-data-grid";
import { theme } from "config";
import { Curriculum } from "models";
import React from "react";
import { ICurriculum } from "types";
import { SortTypes } from "types/filter";

export const useClickOutside = (ref: React.MutableRefObject<any>, handler: (arg0: any) => void) => {
  React.useEffect(() => {
    let startedInside = false;
    let startedWhenMounted = false;

    const listener = (event: MouseEvent) => {
      // Do nothing if `mousedown` or `touchstart` started inside ref element
      if (startedInside || !startedWhenMounted) return;
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) return;

      handler && handler(event);
    };

    const validateEventStart = (event: MouseEvent) => {
      startedWhenMounted = ref.current;
      startedInside = ref.current && ref.current.contains(event.target);
    };

    document.addEventListener("mousedown", validateEventStart);
    document.addEventListener("touchstart", validateEventStart);
    document.addEventListener("click", listener);

    return () => {
      document.removeEventListener("mousedown", validateEventStart);
      document.removeEventListener("touchstart", validateEventStart);
      document.removeEventListener("click", listener);
    };
  }, [ref, handler]);
};

/**
 * Debounces a callback function with a given delay. Usage within a functional component:
 * 
 * const clbk = (arg: any) => { ... };
 * const debouncedClbk = React.useCallback(debounce(clbk), []);
 * 
 * If clbk comes from props:
 * 
 * const debouncedClbk = React.useCallback(debounce(props.clbk), [props.clbk]);
 * 
 * Then use it as youn would normally use the clbk, e.g:
 * 
 * React.useEffect(() => debouncedChange(arg), [someState]);
 * 
 * @param clbk          (args: any) => void | Callback function to be delayed
 * @param debounceDelay number              | Delay in ms. Default is 500ms
 */
export const debounce = (clbk: (args?: any) => void, debounceDelay = 500) => { 
  let debounceTimer: NodeJS.Timeout = null;
  return (args?: any) => {
    clearTimeout(debounceTimer); 
    debounceTimer = setTimeout(() => clbk(args), debounceDelay); 
  }
}


/**
 * @return true if @a and @b have the same values. Objects are compared deeply.
 */
export const deepCmp = (a: any, b: any): boolean => {
  // If a and b point towards the same object, return true.
  if(a === b) return true;

  if(typeof a === "object" && a && b) {
    for(const key in a) if(!deepCmp(a[key], b[key])) return false;
    for(const key in b) if(!(key in a)) return false;
    return true;
  } else 
    return a === b;
}

/**
 * @return a hex color string thats generated pseudo-randomly from a string
 */
export const stringToColor = (string: string): string => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}
/**
 * @return a palatte color that is generated from the passed string
 */
export const stringToPaletteColor = (string: string): string => {
  let hash = 0;

  const theme_colors = [
    theme.palette.blue,
    theme.palette.green,
    theme.palette.magenta,
    theme.palette.lightBlue,
    theme.palette.lightGreen,
    theme.palette.purple,
  ];
  if(!string) return theme_colors[0].main;
  for (let i = 0; i < string.length; i += 1) {
    hash += string.charCodeAt(i);
  }

  return theme_colors[hash % 6].main;
}

/**
 * @return a 1 to 2 upper case character string with the intials of @name
 * 
 * If @name has only one word, the first letter of that is used. 
 * If it has mulitple words, the first letter of the first and the last are used. 
 */
export const toInitials = (name: string): string => {
  if(!name) return "";
  const names = name.split(" ");
  return (names.length > 1 ? names[0][0] + names[names.length-1][0] : names[0][0]).toUpperCase();
}

export const passwordValidation = (password: string): boolean => {
  const validPassword =
    /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))$/;
  return validPassword.test(password) || password === "";
};

export const emailValidation = (email: string): boolean => {
  const validEmail =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return validEmail.test(email) || email === "";
};

export const compare = <T extends {title: string, progress?: number} = Curriculum<ICurriculum>>(a: T, b: T, sort: SortTypes): number => {
  switch (sort) {
    case SortTypes.Alphabetical: {
      const numA = parseInt(a.title.match(/\d+/)?.[0] || "0");
      const numB = parseInt(b.title.match(/\d+/)?.[0] || "0");

      return numA !== numB ? numA - numB : a.title.localeCompare(b.title);
    }
    case SortTypes.Progress:
      return a.progress- b.progress;
    default:
      return 0;
  }
};

export const caseInsensitiveComparator: GridComparatorFn = (v1, v2): number => {
  if (v1 > v2)
    return 1;
  return -1;
};
